<template>
  <div
    ref="faderContainer"
    class="flex flex-col flex-1 select-none"
    style="touch-action: none"
    @touchmove.stop.prevent>
    <!-- Label + Score at top - tappable to expand -->
    <button
      class="flex flex-col items-center shrink-0 mb-1 w-full py-1 rounded-lg transition-colors cursor-pointer"
      :class="
        expanded && dialEnabled ? 'bg-white/10' : 'active:bg-white/5'
      "
      @click.stop="onScoreLabelTap">
      <span
        class="text-[10px] font-medium leading-tight transition-colors"
        :class="expanded && dialEnabled ? 'text-white' : 'text-gray-400'">
        {{ shortLabel }}
      </span>
      <span
        class="text-xs font-medium tabular-nums transition-colors"
        :style="{ color: hslColor }">
        {{ displayValue }}
      </span>
    </button>

    <!-- Fader track area -->
    <div
      class="relative flex-1 w-full"
      @pointerdown.stop="onPointerDown"
      @pointermove.stop="onPointerMove"
      @pointerup.stop="onPointerUp"
      @pointercancel.stop="onPointerUp">
      <div
        ref="trackRef"
        class="relative rounded-sm bg-gray-700 w-full h-full overflow-visible">
        <!-- Filled portion (from bottom) -->
        <div
          class="absolute bottom-0 left-0 w-full rounded-b-sm pointer-events-none transition-colors"
          :style="{
            height: fillPercent + '%',
            backgroundColor: hslColor
          }" />

        <!-- Thumb marker at fill position -->
        <div
          class="absolute left-0 w-full h-3 pointer-events-none rounded-t-sm shadow-lg"
          :style="{
            bottom:
              'calc(' +
              fillPercent +
              '% - ' +
              (fillPercent / 100) * 12 +
              'px)',
            backgroundColor: thumbColor
          }" />

        <!-- Step markers — clipped to unfilled area only -->
        <div
          class="absolute inset-0 pointer-events-none"
          :style="{
            clipPath: 'inset(0 0 ' + fillPercent + '% 0)'
          }">
          <div
            v-for="tick in tickMarks"
            :key="tick.value"
            class="absolute left-0 w-full"
            :style="{
              bottom: tick.value / 10 * 100 + '%',
              transform: 'translateY(50%)'
            }">
            <div
              class="w-full"
              :class="
                tick.major
                  ? 'h-px bg-gray-600'
                  : 'h-px bg-gray-600/30'
              " />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useReviewColors } from '@/use/useReviewColors'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { Capacitor } from '@capacitor/core'

const { score, hslColor } = useReviewColors()

const props = defineProps({
  label: String,
  model: Number,
  expanded: { type: Boolean, default: false },
  dialEnabled: { type: Boolean, default: false }
})

const emit = defineEmits([
  'update:model',
  'request-expand',
  'request-collapse'
])

const localModel = ref(props.model)
const faderContainer = ref(null)
const trackRef = ref(null)
const activePointerId = ref(null)

const SHORT_LABELS = {
  Story: 'Story',
  Characters: 'Char',
  Execution: 'Exec',
  Entertainment: 'Enjoy',
  Impact: 'Impact',
  Universe: 'World'
}

const shortLabel = computed(() => SHORT_LABELS[props.label] || props.label)

const displayValue = computed(() => Number(localModel.value).toFixed(1))

const fillPercent = computed(() => (Number(localModel.value) / 10) * 100)

// Brighter color variant for the thumb
const thumbColor = computed(() => {
  const base = hslColor.value
  const match = base.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
  if (!match) return base
  const h = match[1]
  const s = Math.min(100, parseInt(match[2]) + 10)
  const l = Math.min(90, parseInt(match[3]) + 25)
  return `hsl(${h}, ${s}%, ${l}%)`
})

// Step markers at 0.5 intervals
const tickMarks = computed(() => {
  const result = []
  for (let i = 0; i <= 20; i++) {
    const value = i * 0.5
    result.push({ value, major: value === Math.floor(value) })
  }
  return result
})

// Tap on score or label to toggle expanded mode
const onScoreLabelTap = () => {
  if (!props.dialEnabled) return
  if (props.expanded) {
    emit('request-collapse')
  } else {
    emit('request-expand')
  }
}

// Pointer events for track dragging
// Multi-touch: each fader instance independently captures its own pointer,
// so multiple faders can be dragged simultaneously with different fingers
const onPointerDown = (e) => {
  if (activePointerId.value !== null) return
  e.preventDefault()
  activePointerId.value = e.pointerId
  e.currentTarget.setPointerCapture(e.pointerId)
  emit('request-collapse')
  updateValueFromPointer(e)
}

const onPointerMove = (e) => {
  if (e.pointerId !== activePointerId.value) return
  e.preventDefault()
  updateValueFromPointer(e)
}

const onPointerUp = (e) => {
  if (e.pointerId !== activePointerId.value) return
  activePointerId.value = null
}

const updateValueFromPointer = (e) => {
  const track = trackRef.value
  if (!track) return
  const rect = track.getBoundingClientRect()
  // Vertical: bottom = 0, top = 10
  const y = Math.max(0, Math.min(rect.bottom - e.clientY, rect.height))
  const ratio = y / rect.height
  const rawValue = ratio * 10
  const snapped = Math.round(rawValue * 10) / 10
  updateModel(snapped)
}

const updateModel = (value) => {
  const numValue = Number(value)
  emit('update:model', numValue)
  localModel.value = numValue
  score.value = numValue
  triggerHapticFeedback()
}

const triggerHapticFeedback = () => {
  if (Capacitor.getPlatform() === 'ios') {
    Haptics.impact({ style: ImpactStyle.Light })
  }
}

onMounted(() => {
  score.value = props.model
})

watch(
  () => props.model,
  (newVal) => {
    localModel.value = newVal
    score.value = newVal
  }
)
</script>
